/**
 * The launcher: a vertical tab on the screen edge, rendered in an open shadow
 * root on a host element appended to <html> (not <body>, which SPA routers and
 * page-transition libraries replace). The host is shown in the top layer via
 * the Popover API where supported, so no page stacking context or z-index war
 * can bury it; otherwise it falls back to the maximum z-index.
 */

import { WIDGET_HOST_CLASS, WIDGET_ROOT_ATTRIBUTE } from "@/lib/site-feedback/shared/vocabulary";
import { adoptStyles, el, FEEDBACK_ICON, icon, lockStyles } from "../dom";
import type { WidgetConfig } from "../runtime";

const LAUNCHER_CSS = `
:host { all: initial; }
.sf-launcher {
  position: fixed; top: 50%; z-index: 2147483647; pointer-events: auto;
  transform: translateY(-50%);
  font-family: system-ui, -apple-system, "Segoe UI", sans-serif;
}
.sf-launcher.sf-right { right: 0; }
.sf-launcher.sf-left { left: 0; }
.sf-launcher[hidden] { display: none; }
.sf-tab {
  display: flex; align-items: center; gap: 8px;
  writing-mode: vertical-rl; transform: rotate(180deg);
  margin: 0; padding: 14px 9px; border: 0;
  background: var(--sf-accent, #1F2026); color: var(--sf-on-accent, #FFFFFF);
  font-family: inherit; font-size: 13px; font-weight: 600; line-height: 1.2; letter-spacing: .01em; cursor: pointer;
  box-shadow: 0 4px 18px rgba(31, 32, 38, .28);
  transition: padding .15s ease;
}
.sf-right .sf-tab { border-radius: 0 10px 10px 0; }
.sf-left .sf-tab { border-radius: 10px 0 0 10px; }
.sf-tab svg { transform: rotate(90deg); flex: none; }
.sf-tab:hover { padding-block: 16px; }
.sf-right .sf-tab:hover { padding-right: 12px; }
.sf-left .sf-tab:hover { padding-left: 12px; }
.sf-tab:focus-visible { outline: 3px solid #1E6FD9; outline-offset: 2px; }
.sf-tab[aria-busy="true"] { cursor: progress; opacity: .75; }
@media (prefers-reduced-motion: reduce) { .sf-tab { transition: none; } }
@media print { .sf-launcher { display: none; } }
`;

export interface Launcher {
  host: HTMLElement;
  root: ShadowRoot;
  setVisible(visible: boolean): void;
  setBusy(busy: boolean): void;
  destroy(): void;
}

export function mountLauncher(config: WidgetConfig, nonce: string | undefined, onActivate: () => void): Launcher {
  const host = document.createElement("div");
  host.setAttribute(WIDGET_ROOT_ATTRIBUTE, "");
  host.className = WIDGET_HOST_CLASS;
  host.setAttribute("aria-live", "off");
  lockStyles(host, {
    all: "initial",
    position: "fixed",
    inset: "0",
    width: "100%",
    height: "100%",
    margin: "0",
    padding: "0",
    border: "0",
    background: "transparent",
    overflow: "visible",
    "pointer-events": "none",
    "z-index": "2147483647",
    display: "block",
    "color-scheme": "normal",
  });
  host.style.setProperty("--sf-accent", config.button.color);
  host.style.setProperty("--sf-on-accent", config.button.textColor);

  const root = host.attachShadow({ mode: "open" });
  adoptStyles(root, LAUNCHER_CSS, nonce);

  const label = el("span", {}, [config.button.label]);
  const button = el("button", { type: "button", class: "sf-tab", "aria-label": `${config.button.label}: report an issue on this page` }, [
    icon(FEEDBACK_ICON),
    label,
  ]);
  button.addEventListener("click", () => onActivate());
  const wrapper = el("div", { class: `sf-launcher sf-${config.button.position}` }, [button]);
  root.append(wrapper);

  const attach = () => {
    if (!host.isConnected) document.documentElement.append(host);
    const popoverHost = host as HTMLElement & { showPopover?: () => void };
    if (typeof popoverHost.showPopover === "function") {
      try {
        if (!host.hasAttribute("popover")) host.setAttribute("popover", "manual");
        if (!host.matches(":popover-open")) popoverHost.showPopover();
      } catch {
        /* not supported or already open; z-index fallback applies */
      }
    }
  };
  attach();

  // Re-attach if the page removes us (some routers rebuild <html> children).
  // Known limitation: while the host page has a MODAL <dialog> open (via
  // showModal), the browser makes everything outside that dialog inert,
  // including us; the reviewer closes the dialog first. (Verified 2026-10-02;
  // re-showing the popover does not help, and moving our host into a
  // third-party dialog risks breaking its layout and focus trap.)
  const observer = new MutationObserver(() => {
    if (!host.isConnected) attach();
  });
  observer.observe(document.documentElement, { childList: true });

  return {
    host,
    root,
    setVisible(visible) {
      wrapper.hidden = !visible;
    },
    setBusy(busy) {
      button.setAttribute("aria-busy", busy ? "true" : "false");
      button.disabled = busy;
    },
    destroy() {
      observer.disconnect();
      host.remove();
    },
  };
}
